import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import {
  skipToken,
  useIsMutating,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { Fingerprint, Plus, Pencil, Trash2, KeyRound, Copy, Check, Loader2 } from "lucide-react";
import { Dialog, useAnyDialogOpen } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import {
  usePasskeys,
  usePreparePasskeyRegistration,
  useEnrollPasskey,
  useRenamePasskey,
  useDeletePasskey,
  useRegenerateRecoveryCodes,
  useRecoveryCodesAtRisk,
  usePendingPasskeyRecoveryCodes,
  clearPendingPasskeyRecoveryCodes,
  recoveryCodesAtRiskKey,
  setRecoveryCodesAtRisk,
  useDismissedConfirmation,
  usePublishDismissedConfirmation,
  clearDismissedConfirmation,
  dismissedConfirmationNoticeId,
  readDismissedConfirmation,
  passkeysKeys,
  passkeysMutationKeys,
  consumeProvenPasskeysStepUpRejections,
  hasLatestProvenPasskeysStepUpRejection,
  isPasskeysActionMutationKey,
  passkeysReportedStepUpRejectionIdsKey,
  PreparedRegistrationExpiredError,
  PREPARED_REGISTRATION_LIFETIME_MS,
  useEnrollExpired,
  publishEnrollAbandoned,
  clearEnrollAbandoned,
  publishEnrollExpired,
  clearEnrollExpired,
  signalAbandonedPreparedRegistration,
  type PreparedPasskeyRegistration,
} from "../hooks/usePasskeys";
import { totpMutationKeys } from "../hooks/useTotp";
import { listPasskeys, type Passkey, type PasskeyListResponse } from "../api/passkeys.api";
import {
  ApiError,
  isSessionEndedError,
  isStaleSessionError,
  isUncertainWriteFailure,
  isUncertainWriteTimeout,
} from "@/lib/api/client";
import { cancelCeremonies, isCeremonyActive, subscribeCeremonyActivity } from "@/lib/webauthn";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { useNetworkOnline } from "@/hooks/useNetworkOnline";
import { defaultPasskeyEnrolName, formatDate } from "@/lib/formatters";
import { useStepUp, type StepUpPromptFollows } from "../hooks/useStepUp";
import { useMfaRecoveryHold, useReportPasskeysSectionPresence } from "../mfa-recovery-hold";
import { focusMainTransiently } from "../focus-main";
import { EnrolOutcomeNotice } from "./EnrolOutcomeNotice";
import { StepUpDialog } from "./StepUpDialog";
import { StaleSessionNotice } from "./StaleSessionNotice";
import { FeatureCheckFailedCard } from "./FeatureCheckFailedCard";
import { isStepUpRequired, StepUpAbandonedError, type StepUpProof } from "../api/stepup.api";
import { passkeyEnrolmentReplacesCodes } from "../recovery-code-replacement";
import { ackRecoveryCodesShown } from "../recovery-codes-generation";

export function PasskeysSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, error, refetch, isFetching, isPaused, dataUpdatedAt } = usePasskeys();
  const online = useNetworkOnline();
  const ceremonyOwner = useCeremonyOwner();
  const prepare = usePreparePasskeyRegistration(ceremonyOwner);
  const enroll = useEnrollPasskey();
  const rename = useRenamePasskey();
  const remove = useDeletePasskey();
  const regenerate = useRegenerateRecoveryCodes();
  const recoveryCodesAtRisk = useRecoveryCodesAtRisk();

  const supportsWebAuthn = browserSupportsWebAuthn();
  const ceremonyActive = useSyncExternalStore(subscribeCeremonyActivity, () =>
    isCeremonyActive(ceremonyOwner),
  );
  const anyDialogOpen = useAnyDialogOpen();
  const stepUp = useStepUp(ceremonyOwner);
  const dismissedConfirmation = useDismissedConfirmation();
  const [renameTarget, setRenameTarget] = useState<Passkey | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Passkey | null>(null);
  const [deleteOverdue, setDeleteOverdue] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [enrollNameOpen, setEnrollNameOpen] = useState(false);
  const [enrollNameDraft, setEnrollNameDraft] = useState("");
  const selectEnrollNameOnFocusRef = useRef(false);
  const [newName, setNewName] = useState("");
  const [shownCodes, setShownCodes] = useState<string[] | null>(null);
  const pendingCodes = usePendingPasskeyRecoveryCodes();
  const visibleCodes = shownCodes ?? pendingCodes;
  const [codesWarnedBeforeAction, setCodesWarnedBeforeAction] = useState(false);
  const [copied, setCopied] = useState(false);
  const enrollExpiredNotice = useEnrollExpired();
  const [enrollStepUpActive, setEnrollStepUpActive] = useState(false);
  const [enrollFollowsRejection, setEnrollFollowsRejection] = useState(false);
  const [stepUpSeenForPending, setStepUpSeenForPending] = useState(false);
  const dismissedRequiredRef = useRef(false);
  const pendingRegistrationRef = useRef<PreparedPasskeyRegistration | null>(null);
  const enrollLapsedByTimerRef = useRef(false);
  const uncertainListSinceRef = useRef<number | null>(null);
  const [suppressUncertainEnrollFailed, setSuppressUncertainEnrollFailed] = useState(false);
  const uncertainRegistrationRef = useRef<PreparedPasskeyRegistration | null>(null);
  useReportPasskeysSectionPresence();
  const [checkRetryFailed, setCheckRetryFailed] = useState(false);
  const [checkRetrying, setCheckRetrying] = useState(false);
  const [checkFailedShown, setCheckFailedShown] = useState(false);
  const [dismissedNoticeShown, setDismissedNoticeShown] = useState(false);
  const [rejectionNoticeLatched, setRejectionNoticeLatched] = useState(false);
  const [confirmFollowsRejection, setConfirmFollowsRejection] = useState(false);
  const [removal, setRemoval] = useState<{ passkey: Passkey; landed: boolean } | null>(null);
  const enrollOnWire = useIsMutating({ mutationKey: passkeysMutationKeys.enroll }) > 0;
  const [enrollRefreshingList, setEnrollRefreshingList] = useState(false);
  const enrollVerifyOnWireRef = useRef(false);
  const removingPasskeyIds = useMutationState({
    filters: { mutationKey: passkeysMutationKeys.remove, status: "pending" },
    select: (mutation) => (mutation.state.variables as typeof remove.variables)?.passkeyId,
  });
  const removePaused = useMutationState({
    filters: { mutationKey: passkeysMutationKeys.remove, status: "pending" },
    select: (mutation) => mutation.state.isPaused,
  }).some(Boolean);
  const regenerateOnWire = useIsMutating({ mutationKey: passkeysMutationKeys.regenerate }) > 0;
  const actionMutationTick = useMutationState({
    filters: {
      predicate: (mutation) => isPasskeysActionMutationKey(mutation.options.mutationKey),
    },
    select: (mutation) => `${mutation.mutationId}:${mutation.state.status}`,
  });
  void actionMutationTick;
  const { data: reportedStepUpRejectionIds = [] } = useQuery({
    queryKey: passkeysReportedStepUpRejectionIdsKey,
    queryFn: skipToken,
    initialData: [] as number[],
    staleTime: Infinity,
    gcTime: Infinity,
  });
  void reportedStepUpRejectionIds;
  const cacheProvenRejection = hasLatestProvenPasskeysStepUpRejection(queryClient);
  const rejectionOutstanding =
    stepUp.dismissedRequired ||
    cacheProvenRejection ||
    (rejectionNoticeLatched && dismissedConfirmation === "passkeys");
  usePublishDismissedConfirmation(
    "passkeys",
    stepUp.dismissedRequired || cacheProvenRejection,
    stepUp.acknowledgeDismissed,
  );
  const totpVerifyOnWire = useIsMutating({ mutationKey: totpMutationKeys.verify }) > 0;
  const totpRemoveOnWire = useIsMutating({ mutationKey: totpMutationKeys.remove }) > 0;
  const pendingActionId = useId();
  const otherPendingId = useId();
  const removePausedId = useId();
  const reportHold = useMfaRecoveryHold("passkey");
  const mountedRef = useRef(true);
  const layoutShownRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restoreHeadingRef = useRef(false);
  const removalRegionRef = useRef<HTMLParagraphElement>(null);
  const announcedRemovalRef = useRef<{ passkey: Passkey; landed: boolean } | null>(null);
  const removalOutcomeId = useId();
  const rowDatesId = useId();

  const showCodes = (codes: string[] | null) => {
    if (!mountedRef.current) return;
    setShownCodes(codes);
    reportHold(codes !== null);
  };

  const dismissCodes = () => {
    clearPendingPasskeyRecoveryCodes(queryClient);
    showCodes(null);
  };

  useLayoutEffect(() => {
    if (visibleCodes === null) return;
    const title = t("account.passkeys.recoveryCodesTitle");
    const codesDialogOpen = [...document.querySelectorAll("dialog[open]")].some(
      (dialog) => dialog.querySelector("h2")?.textContent === title,
    );
    if (!codesDialogOpen) return;
    setRecoveryCodesAtRisk(queryClient, false);
    reportHold(true);
    void ackRecoveryCodesShown(queryClient).then(() => {
      void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
    });
  }, [visibleCodes, queryClient, reportHold, t]);

  useEffect(() => {
    enrollVerifyOnWireRef.current = stepUp.proofOnWire || enrollOnWire || enrollRefreshingList;
  }, [stepUp.proofOnWire, enrollOnWire, enrollRefreshingList]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      reportHold(false);
      const registration = pendingRegistrationRef.current;
      if (!registration || enrollVerifyOnWireRef.current) return;
      const lapsed = enrollLapsedByTimerRef.current;
      enrollLapsedByTimerRef.current = false;
      pendingRegistrationRef.current = null;
      if (lapsed) {
        clearEnrollAbandoned(queryClient);
        publishEnrollExpired(queryClient);
      } else {
        clearEnrollExpired(queryClient);
        publishEnrollAbandoned(queryClient);
      }
      signalAbandonedPreparedRegistration(registration);
    };
  }, [reportHold, queryClient]);

  useEffect(() => {
    return () => {
      if (readDismissedConfirmation(queryClient) === "passkeys") {
        clearDismissedConfirmation(queryClient);
      }
    };
  }, [queryClient]);

  useEffect(() => {
    dismissedRequiredRef.current = stepUp.dismissedRequired;
  }, [stepUp.dismissedRequired]);

  useEffect(() => {
    const since = uncertainListSinceRef.current;
    const waitingAfterReset = enroll.error == null && since !== null;
    if (!isUncertainWriteFailure(enroll.error) && !waitingAfterReset) {
      if (enroll.error == null) uncertainRegistrationRef.current = null;
      return;
    }
    if (since === null) return;
    if (isFetching || isPaused) return;
    if (dataUpdatedAt < since) return;
    if (data?.mfaEnabled !== false && data?.mfaEnabled !== true) return;
    const registration = uncertainRegistrationRef.current;
    uncertainListSinceRef.current = null;
    uncertainRegistrationRef.current = null;
    if (data.mfaEnabled === false) {
      clearEnrollExpired(queryClient);
      publishEnrollAbandoned(queryClient);
      signalAbandonedPreparedRegistration(registration);
    } else {
      clearEnrollAbandoned(queryClient);
    }
  }, [data, dataUpdatedAt, enroll.error, isFetching, isPaused, queryClient]);

  const passkeys = data?.passkeys ?? [];
  const mfaEnabled = data?.mfaEnabled ?? false;
  const totpEnabled = data?.totpEnabled ?? false;
  const remainingCodes = data?.remainingRecoveryCodes ?? 0;
  const listUnchecked = data === undefined && (error !== null || isPaused);
  const listSessionEnded = listUnchecked && isSessionEndedError(error);
  const waitingForNetwork = data === undefined && !online;
  if (
    removal !== null &&
    !removal.landed &&
    !anyDialogOpen &&
    !passkeys.some((passkey) => passkey.id === removal.passkey.id)
  ) {
    setRemoval({ passkey: removal.passkey, landed: true });
  }
  const landedRemoval = removal?.landed ? removal : null;
  const removalOutcome =
    landedRemoval === null
      ? null
      : t("account.passkeys.deleted", { name: landedRemoval.passkey.name });
  useLayoutEffect(() => {
    const region = removalRegionRef.current;
    if (landedRemoval === null) {
      if (region !== null) region.textContent = "";
      return;
    }
    if (announcedRemovalRef.current === landedRemoval) return;
    announcedRemovalRef.current = landedRemoval;
    const active = document.activeElement;
    if (active === null || active === document.body) headingRef.current?.focus();
    else if (region !== null) region.textContent = removalOutcome;
  }, [landedRemoval, removalOutcome]);
  const settlingPasskeyId =
    removal !== null && !removal.landed && isFetching ? removal.passkey.id : undefined;
  const removingPasskeyId = removingPasskeyIds[0] ?? settlingPasskeyId;
  const removeOnWire = removingPasskeyId !== undefined;
  const forgetLandedRemoval = () => setRemoval((current) => (current?.landed ? null : current));

  const enrollPending = prepare.isPending || enrollOnWire || enrollRefreshingList;
  const pendingActionLabel = enrollPending
    ? t("account.passkeys.enrollWorking")
    : removeOnWire
      ? t("account.passkeys.deleting")
      : regenerateOnWire
        ? t("account.passkeys.regenerating")
        : null;
  const actionPending = pendingActionLabel !== null;
  const otherPendingLabel = totpVerifyOnWire
    ? t("account.totp.verifying")
    : totpRemoveOnWire
      ? t("account.totp.removing")
      : null;
  const otherPending = otherPendingLabel !== null;
  const addBlockedByTotpRejection = dismissedConfirmation === "totp";
  if (!actionPending) {
    if (stepUpSeenForPending) setStepUpSeenForPending(false);
  } else if (stepUp.open) {
    if (!stepUpSeenForPending) setStepUpSeenForPending(true);
  }
  const earlierRejection = confirmFollowsRejection
    ? `${t("account.stepUp.earlierActionIncomplete")} `
    : "";
  const enrollError = prepare.error ?? enroll.error;
  const enrollBrowserError = enrollError && !(enrollError instanceof ApiError) ? enrollError : null;
  const namedMutationError = (
    [
      [enrollError, "account.passkeys.add"],
      [rename.error, "account.passkeys.rename"],
      [remove.error, "account.passkeys.delete"],
      [regenerate.error, "account.passkeys.regenerate"],
    ] as const
  ).find(
    (entry): entry is [ApiError, (typeof entry)[1]] =>
      entry[0] instanceof ApiError && entry[0].code !== "step_up_required",
  );
  const mutationApiError = namedMutationError?.[0];
  const staleSession = isStaleSessionError(mutationApiError);
  const removeUnanswered = remove.error instanceof TypeError;
  const sectionCancelReachable = ceremonyActive && !anyDialogOpen;
  const showSectionBusy =
    stepUpSeenForPending &&
    actionPending &&
    settlingPasskeyId === undefined &&
    !enrollPending &&
    !anyDialogOpen &&
    !stepUp.open;
  const codesWarningCarried = recoveryCodesAtRisk && mfaEnabled;
  const mutationRaisedCodesWarning = (err: ApiError) =>
    !codesWarnedBeforeAction &&
    isUncertainWriteFailure(err) &&
    (err === regenerate.error ||
      (err === enroll.error && enroll.variables?.replacesRecoveryCodes === true));

  const { reset: resetPrepare } = prepare;
  const { reset: resetFinish } = enroll;
  const { reset: resetRename } = rename;
  const { reset: resetRemove } = remove;
  const { reset: resetRegenerate } = regenerate;
  const clearOtherActionErrors = useCallback(
    (keep: "enroll" | "rename" | "remove" | "regenerate") => {
      const resetFailed = (error: unknown, reset: () => void) => {
        if (!error || isStaleSessionError(error)) return;
        reset();
      };
      if (keep !== "enroll") {
        resetFailed(prepare.error, resetPrepare);
        resetFailed(enroll.error, resetFinish);
      }
      if (keep !== "rename") resetFailed(rename.error, resetRename);
      if (keep !== "remove") resetFailed(remove.error, resetRemove);
      if (keep !== "regenerate") resetFailed(regenerate.error, resetRegenerate);
    },
    [
      prepare.error,
      enroll.error,
      rename.error,
      remove.error,
      regenerate.error,
      resetPrepare,
      resetFinish,
      resetRename,
      resetRemove,
      resetRegenerate,
    ],
  );

  const resetEnroll = useCallback(() => {
    const registration = pendingRegistrationRef.current;
    const lapsed = enrollLapsedByTimerRef.current;
    enrollLapsedByTimerRef.current = false;
    pendingRegistrationRef.current = null;
    if (mountedRef.current) setEnrollStepUpActive(false);
    resetPrepare();
    resetFinish();
    setSuppressUncertainEnrollFailed(false);
    if (lapsed) {
      clearEnrollAbandoned(queryClient);
      publishEnrollExpired(queryClient);
    } else {
      clearEnrollExpired(queryClient);
      publishEnrollAbandoned(queryClient);
    }
    signalAbandonedPreparedRegistration(registration);
    if (!mountedRef.current) return;
    reportHold(false);
  }, [resetPrepare, resetFinish, reportHold, queryClient]);

  const clearRejectionReport = useCallback(() => {
    stepUp.acknowledgeDismissed();
    setRejectionNoticeLatched(false);
    setDismissedNoticeShown(false);
    consumeProvenPasskeysStepUpRejections(queryClient);
  }, [stepUp, queryClient]);

  const openEnrollName = () => {
    if (otherPending || addBlockedByTotpRejection || enrollPending || enrollNameOpen) return;
    forgetLandedRemoval();
    setEnrollNameDraft(defaultPasskeyEnrolName());
    selectEnrollNameOnFocusRef.current = true;
    setEnrollNameOpen(true);
  };

  const handleEnroll = (name: string) => {
    const enrolName = name.trim().slice(0, 100);
    if (!enrolName || otherPending || addBlockedByTotpRejection) return;
    setEnrollFollowsRejection((prev) => prev || rejectionOutstanding);
    clearRejectionReport();
    clearEnrollAbandoned(queryClient);
    clearEnrollExpired(queryClient);
    uncertainListSinceRef.current = null;
    setSuppressUncertainEnrollFailed(false);
    uncertainRegistrationRef.current = null;
    pendingRegistrationRef.current = null;
    enrollLapsedByTimerRef.current = false;
    forgetLandedRemoval();
    clearOtherActionErrors("enroll");
    enroll.reset();
    if (mountedRef.current) reportHold(true);
    void prepare.mutateAsync().then(
      (registration) => {
        if (!mountedRef.current) {
          clearEnrollExpired(queryClient);
          publishEnrollAbandoned(queryClient);
          signalAbandonedPreparedRegistration(registration);
          return;
        }
        if (
          queryClient.isMutating({ mutationKey: totpMutationKeys.verify }) > 0 ||
          queryClient.isMutating({ mutationKey: totpMutationKeys.remove }) > 0
        ) {
          pendingRegistrationRef.current = registration;
          resetEnroll();
          return;
        }
        pendingRegistrationRef.current = registration;
        setEnrollStepUpActive(true);
        setEnrollFollowsRejection((prev) => prev || dismissedRequiredRef.current);
        const listNow = queryClient.getQueryData<PasskeyListResponse>(passkeysKeys.list());
        void stepUp.run(performEnroll(registration, enrolName), {
          needsProof: listNow?.mfaEnabled ?? mfaEnabled,
          dashboardOnly: true,
          onDismiss: resetEnroll,
        });
      },
      () => {
        if (mountedRef.current) reportHold(false);
      },
    );
  };

  const performEnroll = (registration: PreparedPasskeyRegistration, name: string) => {
    return async (proof: StepUpProof | undefined, stepUpPromptFollows: StepUpPromptFollows) => {
      if (mountedRef.current) reportHold(true);
      if (mountedRef.current) setEnrollRefreshingList(true);
      let list: PasskeyListResponse | undefined;
      try {
        try {
          list = await listPasskeys();
        } catch {
          list = queryClient.getQueryData<PasskeyListResponse>(passkeysKeys.list());
        }
        if (!mountedRef.current || pendingRegistrationRef.current !== registration) {
          if (mountedRef.current) reportHold(false);
          return;
        }
        const replacesRecoveryCodes = passkeyEnrolmentReplacesCodes(
          list,
          list?.mfaEnabled ?? mfaEnabled,
        );
        setCodesWarnedBeforeAction(queryClient.getQueryData(recoveryCodesAtRiskKey) === true);
        return await enroll
          .mutateAsync({ registration, name, stepUp: proof, replacesRecoveryCodes })
          .then(
            (result) => {
              pendingRegistrationRef.current = null;
              enrollLapsedByTimerRef.current = false;
              if (mountedRef.current) setEnrollStepUpActive(false);
              clearEnrollAbandoned(queryClient);
              clearEnrollExpired(queryClient);
              setSuppressUncertainEnrollFailed(false);
              if (readDismissedConfirmation(queryClient) === "passkeys") {
                clearDismissedConfirmation(queryClient);
              }
              if (mountedRef.current) setEnrollFollowsRejection(false);
              if (result.recoveryCodes) showCodes(result.recoveryCodes);
              else if (mountedRef.current) reportHold(false);
            },
            (error: unknown) => {
              const keepsHoldForStepUp = isStepUpRequired(error) && stepUpPromptFollows();
              if (
                isStepUpRequired(error) &&
                !keepsHoldForStepUp &&
                pendingRegistrationRef.current === registration
              ) {
                pendingRegistrationRef.current = null;
                const lapsed = enrollLapsedByTimerRef.current;
                enrollLapsedByTimerRef.current = false;
                if (mountedRef.current) setEnrollStepUpActive(false);
                if (lapsed) {
                  clearEnrollAbandoned(queryClient);
                  publishEnrollExpired(queryClient);
                } else {
                  clearEnrollExpired(queryClient);
                  publishEnrollAbandoned(queryClient);
                }
                signalAbandonedPreparedRegistration(registration);
              }
              if (mountedRef.current && !keepsHoldForStepUp) reportHold(false);
              if (error instanceof StepUpAbandonedError) throw error;
              if (isStepUpRequired(error)) throw error;
              if (pendingRegistrationRef.current === registration) {
                pendingRegistrationRef.current = null;
                if (mountedRef.current) setEnrollStepUpActive(false);
                if (error instanceof PreparedRegistrationExpiredError) {
                  clearEnrollAbandoned(queryClient);
                  publishEnrollExpired(queryClient);
                  signalAbandonedPreparedRegistration(registration);
                } else if (
                  error instanceof ApiError &&
                  error.code === "registration_challenge_invalid"
                ) {
                  clearEnrollExpired(queryClient);
                  publishEnrollAbandoned(queryClient);
                  signalAbandonedPreparedRegistration(registration);
                } else if (isUncertainWriteFailure(error)) {
                  clearEnrollExpired(queryClient);
                  if (replacesRecoveryCodes) {
                    uncertainListSinceRef.current = Date.now();
                    setSuppressUncertainEnrollFailed(true);
                    uncertainRegistrationRef.current = registration;
                  } else {
                    setSuppressUncertainEnrollFailed(false);
                  }
                } else {
                  uncertainListSinceRef.current = null;
                  setSuppressUncertainEnrollFailed(false);
                  uncertainRegistrationRef.current = null;
                  clearEnrollExpired(queryClient);
                  publishEnrollAbandoned(queryClient);
                  signalAbandonedPreparedRegistration(registration);
                }
              }
              if (replacesRecoveryCodes && isUncertainWriteTimeout(error)) enroll.reset();
            },
          );
      } finally {
        if (mountedRef.current) setEnrollRefreshingList(false);
      }
    };
  };

  useEffect(() => {
    if (!stepUp.dismissedRequired) return;
    const registration = pendingRegistrationRef.current;
    if (!registration) return;
    const lapsed = enrollLapsedByTimerRef.current;
    enrollLapsedByTimerRef.current = false;
    pendingRegistrationRef.current = null;
    if (mountedRef.current) setEnrollStepUpActive(false);
    if (lapsed) {
      clearEnrollAbandoned(queryClient);
      publishEnrollExpired(queryClient);
    } else {
      clearEnrollExpired(queryClient);
      publishEnrollAbandoned(queryClient);
    }
    signalAbandonedPreparedRegistration(registration);
    if (mountedRef.current) reportHold(false);
  }, [stepUp.dismissedRequired, queryClient, reportHold]);

  const cancelStepUp = stepUp.cancel;
  useEffect(() => {
    if (!stepUp.open) return;
    const registration = pendingRegistrationRef.current;
    if (!registration) return;
    if (stepUp.proofOnWire || enrollOnWire || enrollRefreshingList) return;
    const remaining = PREPARED_REGISTRATION_LIFETIME_MS - (Date.now() - registration.startedAt);
    const lapse = () => {
      if (pendingRegistrationRef.current !== registration) return;
      enrollLapsedByTimerRef.current = true;
      cancelStepUp();
    };
    if (remaining <= 0) {
      lapse();
      return;
    }
    const timer = setTimeout(lapse, remaining);
    return () => clearTimeout(timer);
  }, [
    stepUp.open,
    stepUp.nonce,
    cancelStepUp,
    stepUp.proofOnWire,
    enrollOnWire,
    enrollRefreshingList,
  ]);

  useEffect(() => {
    if (stepUp.open || !enrollStepUpActive) return;
    if (enrollOnWire || stepUp.proofOnWire || enrollRefreshingList) return;
    if (pendingRegistrationRef.current !== null) return;
    setEnrollStepUpActive(false);
  }, [stepUp.open, enrollStepUpActive, enrollOnWire, stepUp.proofOnWire, enrollRefreshingList]);

  const handleDeletePasskey = (passkey: Passkey) => {
    if (otherPending) return;
    setDeleteTarget(null);
    void stepUp.run(
      async (proof) => {
        await remove.mutateAsync({ passkeyId: passkey.id, stepUp: proof });
        setRemoval({ passkey, landed: false });
      },
      { onDismiss: remove.reset },
    );
  };

  const handleRegenerate = () => {
    if (otherPending) return;
    setRegenerateOpen(false);
    void stepUp.run(
      (proof) => {
        setCodesWarnedBeforeAction(queryClient.getQueryData(recoveryCodesAtRiskKey) === true);
        return regenerate.mutateAsync(proof ? { stepUp: proof } : undefined).then((result) => {
          showCodes(result.recoveryCodes);
        });
      },
      { onDismiss: regenerate.reset },
    );
  };

  const workOnScreen = anyDialogOpen || enrollPending || visibleCodes !== null || stepUp.open;
  const wantCheckFailed = listUnchecked || (checkFailedShown && data === undefined);
  if (!workOnScreen && checkFailedShown !== wantCheckFailed) {
    setCheckFailedShown(wantCheckFailed);
  }
  const showCheckFailed = workOnScreen ? checkFailedShown : wantCheckFailed;
  const carrierOwnsRetry = recoveryCodesAtRisk && listUnchecked;
  if (checkRetryFailed && (!showCheckFailed || carrierOwnsRetry || waitingForNetwork)) {
    setCheckRetryFailed(false);
  }
  const showDismissedNotice = rejectionOutstanding && (dismissedNoticeShown || !anyDialogOpen);
  const namedDismissal = dismissedConfirmation === "passkeys";
  if (dismissedNoticeShown !== showDismissedNotice) {
    setDismissedNoticeShown(showDismissedNotice);
  }
  const noticePainted = showDismissedNotice && !showCheckFailed;
  if (noticePainted && !rejectionNoticeLatched) {
    setRejectionNoticeLatched(true);
  }
  useEffect(() => {
    if (!rejectionNoticeLatched) return;
    consumeProvenPasskeysStepUpRejections(queryClient);
  }, [rejectionNoticeLatched, queryClient]);
  useEffect(() => {
    if (!showCheckFailed) {
      layoutShownRef.current = true;
      return;
    }
    const active = document.activeElement;
    const strandedInClosedDialog =
      active instanceof HTMLElement && active.closest("dialog:not([open])") !== null;
    const focusWasLost = active === null || active === document.body || strandedInClosedDialog;
    if (layoutShownRef.current && focusWasLost) {
      const moved = focusMainTransiently();
      if (!moved && strandedInClosedDialog && active instanceof HTMLElement) active.blur();
    }
    layoutShownRef.current = false;
  }, [showCheckFailed]);
  useLayoutEffect(() => {
    if (showCheckFailed || !restoreHeadingRef.current) return;
    restoreHeadingRef.current = false;
    headingRef.current?.focus();
    if (document.activeElement !== headingRef.current) focusMainTransiently();
  }, [showCheckFailed]);

  const regenerateFoundMfaOff =
    regenerate.error instanceof ApiError && regenerate.error.code === "recovery_codes_mfa_disabled";
  const listLoaded = data !== undefined;
  useEffect(() => {
    if (!regenerateFoundMfaOff || mfaEnabled || !listLoaded) return;
    const active = document.activeElement;
    if (active === null || active === document.body) headingRef.current?.focus();
  }, [regenerateFoundMfaOff, mfaEnabled, listLoaded]);

  const deleteLabel = (passkey: Passkey) =>
    passkey.id === removingPasskeyId
      ? t("account.passkeys.deletingNamed", { name: passkey.name })
      : t("account.passkeys.deleteNamed", { name: passkey.name });

  const handleCopyCodes = async () => {
    if (!visibleCodes) return;
    try {
      await navigator.clipboard.writeText(visibleCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      {showCheckFailed ? (
        <FeatureCheckFailedCard
          icon={Fingerprint}
          title={t("account.passkeys.title")}
          message={
            listSessionEnded
              ? t("account.passkeys.sessionEnded")
              : t("account.passkeys.checkFailed")
          }
          retrying={checkRetrying || isFetching || isPaused}
          retryFailed={checkRetryFailed}
          waitingForNetwork={waitingForNetwork}
          announceWaiting={checkRetrying && waitingForNetwork}
          sessionEnded={listSessionEnded}
          restoreFocusRef={restoreHeadingRef}
          onRetry={
            carrierOwnsRetry
              ? undefined
              : () => {
                  setCheckRetrying(true);
                  void refetch().then((result) => {
                    setCheckRetrying(false);
                    setCheckRetryFailed(result.isError);
                    if (result.isError) restoreHeadingRef.current = false;
                  });
                }
          }
        />
      ) : (
        <div className="rounded-xl border border-border bg-surface p-6">
          <div className="flex items-center justify-between">
            <h3
              ref={headingRef}
              tabIndex={-1}
              aria-describedby={removalOutcome === null ? undefined : removalOutcomeId}
              className="flex items-center gap-2 text-base font-semibold text-heading"
            >
              <Fingerprint size={18} />
              {t("account.passkeys.title")}
            </h3>
            <button
              type="button"
              onClick={
                !enrollPending
                  ? actionPending || otherPending || addBlockedByTotpRejection || enrollNameOpen
                    ? undefined
                    : openEnrollName
                  : sectionCancelReachable
                    ? () => cancelCeremonies(ceremonyOwner)
                    : undefined
              }
              disabled={!enrollPending && (!supportsWebAuthn || staleSession || data === undefined)}
              aria-disabled={
                (enrollPending
                  ? !sectionCancelReachable
                  : actionPending || otherPending || addBlockedByTotpRejection || enrollNameOpen) ||
                undefined
              }
              aria-describedby={
                !enrollPending && actionPending
                  ? pendingActionId
                  : !enrollPending && otherPending
                    ? otherPendingId
                    : !enrollPending && addBlockedByTotpRejection
                      ? dismissedConfirmationNoticeId
                      : undefined
              }
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {!enrollPending && <Plus size={12} />}
              {!enrollPending
                ? t("account.passkeys.add")
                : sectionCancelReachable
                  ? t("account.passkeys.cancelEnroll")
                  : t("account.passkeys.enrollWorking")}
            </button>
          </div>
          <p aria-live="polite" aria-atomic="true" className="sr-only">
            {enrollPending
              ? ceremonyActive
                ? t("account.passkeys.enrolling")
                : t("account.passkeys.enrollWorking")
              : ""}
          </p>
          <p id={removalOutcomeId} hidden>
            {removalOutcome ?? ""}
          </p>
          <p ref={removalRegionRef} aria-live="polite" aria-atomic="true" className="sr-only" />
          {pendingActionLabel && (
            <p id={pendingActionId} className="sr-only">
              {pendingActionLabel}
            </p>
          )}
          {otherPendingLabel && (
            <p id={otherPendingId} className="sr-only">
              {otherPendingLabel}
            </p>
          )}
          {showSectionBusy && (
            <p className="mt-3 text-xs text-muted">{t("account.passkeys.sectionBusy")}</p>
          )}
          <p className="mt-1 text-xs text-muted">{t("account.passkeys.subtitle")}</p>

          {!supportsWebAuthn && (
            <p role="alert" className="mt-3 text-xs text-muted">
              {t("account.passkeys.unsupported")}
            </p>
          )}

          <ApiErrorBanner error={error} className="mt-4" />
          {removePaused && (
            <p id={removePausedId} role="alert" className="mt-3 text-xs text-muted">
              {t("common.waitingForNetwork")}
            </p>
          )}
          {removeUnanswered && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {t("common.writeTimeout")}
            </p>
          )}
          {mutationApiError &&
            namedMutationError &&
            !staleSession &&
            !(codesWarningCarried && mutationRaisedCodesWarning(mutationApiError)) && (
              <p role="alert" className="mt-3 text-xs text-danger">
                {t("account.passkeys.actionFailed", {
                  action: t(namedMutationError[1]),
                  detail: mutationApiError.error ?? t("common.error"),
                })}
              </p>
            )}
          {staleSession && <StaleSessionNotice returnTo="/account" className="mt-4" autoFocus />}
          {showDismissedNotice && (
            <p
              id={namedDismissal ? dismissedConfirmationNoticeId : undefined}
              role="alert"
              className="mt-3 text-xs text-danger"
            >
              {t("account.stepUp.dismissedIncomplete")}
            </p>
          )}
          {enrollFollowsRejection && !showDismissedNotice && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {t("account.stepUp.earlierActionIncomplete")}
            </p>
          )}
          {enrollBrowserError instanceof PreparedRegistrationExpiredError &&
            !enrollExpiredNotice && (
              <p role="alert" className="mt-3 text-xs text-muted">
                {t("account.passkeys.enrollExpired")}
              </p>
            )}
          {enrollBrowserError &&
            !(enrollBrowserError instanceof PreparedRegistrationExpiredError) &&
            !(
              suppressUncertainEnrollFailed &&
              isUncertainWriteFailure(enroll.error) &&
              enrollBrowserError instanceof TypeError
            ) && (
              <p role="alert" className="mt-3 text-xs text-muted">
                {enrollBrowserError.name === "NotAllowedError" ||
                enrollBrowserError.name === "AbortError"
                  ? t("account.passkeys.enrollCanceled")
                  : t("account.passkeys.enrollFailed")}
              </p>
            )}
          <EnrolOutcomeNotice placement="inline" />

          {data !== undefined && passkeys.length === 0 && (
            <p className="mt-4 text-sm text-muted">{t("account.passkeys.empty")}</p>
          )}

          {passkeys.length > 0 && (
            <ul className="mt-4 divide-y divide-border">
              {passkeys.map((passkey, index) => (
                <li key={passkey.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-heading">{passkey.name}</p>
                    <p id={`${rowDatesId}-${index}`} className="mt-0.5 text-xs text-muted">
                      {passkey.createdAt && (
                        <>
                          {t("account.passkeys.created")}:{" "}
                          {formatDate(new Date(passkey.createdAt).getTime())}
                        </>
                      )}
                      {passkey.lastUsedAt && (
                        <>
                          {" · "}
                          {t("account.passkeys.lastUsed")}:{" "}
                          {formatDate(new Date(passkey.lastUsedAt).getTime())}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        clearOtherActionErrors("rename");
                        forgetLandedRemoval();
                        setRenameTarget(passkey);
                        setNewName(passkey.name);
                      }}
                      title={t("account.passkeys.renameNamed", { name: passkey.name })}
                      aria-label={t("account.passkeys.renameNamed", { name: passkey.name })}
                      aria-describedby={`${rowDatesId}-${index}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (actionPending || otherPending) return;
                        clearOtherActionErrors("remove");
                        forgetLandedRemoval();
                        setEnrollFollowsRejection(false);
                        setConfirmFollowsRejection(
                          rejectionOutstanding || dismissedConfirmation !== null,
                        );
                        clearRejectionReport();
                        if (dismissedConfirmation !== null) clearDismissedConfirmation(queryClient);
                        setDeleteOverdue(
                          data?.mfaSetupDeadline != null && data.mfaSetupDeadline < Date.now(),
                        );
                        setDeleteTarget(passkey);
                      }}
                      title={deleteLabel(passkey)}
                      aria-label={deleteLabel(passkey)}
                      aria-disabled={actionPending || otherPending || undefined}
                      aria-describedby={
                        passkey.id === removingPasskeyId && removePaused
                          ? removePausedId
                          : actionPending && passkey.id !== removingPasskeyId
                            ? `${pendingActionId} ${rowDatesId}-${index}`
                            : otherPending
                              ? `${otherPendingId} ${rowDatesId}-${index}`
                              : `${rowDatesId}-${index}`
                      }
                      aria-busy={passkey.id === removingPasskeyId || undefined}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                    >
                      {passkey.id === removingPasskeyId ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {mfaEnabled && (
            <div className="mt-5 flex items-center justify-between rounded-lg bg-elevated/50 p-4">
              <div>
                <p className="flex items-center gap-1.5 text-sm font-medium text-heading">
                  <KeyRound size={14} />
                  {t("account.passkeys.recoveryCodes")}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {t("account.passkeys.recoveryCodesRemaining", { count: remainingCodes })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (actionPending || otherPending) return;
                  clearOtherActionErrors("regenerate");
                  forgetLandedRemoval();
                  setEnrollFollowsRejection(false);
                  setConfirmFollowsRejection(
                    rejectionOutstanding || dismissedConfirmation !== null,
                  );
                  clearRejectionReport();
                  if (dismissedConfirmation !== null) clearDismissedConfirmation(queryClient);
                  setRegenerateOpen(true);
                }}
                aria-disabled={actionPending || otherPending || undefined}
                aria-busy={regenerateOnWire || undefined}
                aria-describedby={
                  actionPending && !regenerateOnWire
                    ? pendingActionId
                    : otherPending
                      ? otherPendingId
                      : undefined
                }
                className="rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {regenerateOnWire
                  ? t("account.passkeys.regenerating")
                  : t("account.passkeys.regenerate")}
              </button>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={enrollNameOpen}
        onClose={() => setEnrollNameOpen(false)}
        title={t("account.passkeys.enrollNameTitle")}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = enrollNameDraft.trim().slice(0, 100);
            if (!trimmed) return;
            setEnrollNameOpen(false);
            handleEnroll(trimmed);
          }}
        >
          <input
            type="text"
            value={enrollNameDraft}
            onChange={(e) => setEnrollNameDraft(e.target.value)}
            onFocus={(e) => {
              if (!selectEnrollNameOnFocusRef.current) return;
              selectEnrollNameOnFocusRef.current = false;
              e.currentTarget.select();
            }}
            aria-label={t("account.passkeys.nameLabel")}
            data-autofocus
            maxLength={100}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo hover:border-border-light"
          />
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => setEnrollNameOpen(false)}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={() => {
                const chosen = enrollNameDraft.trim().slice(0, 100) || defaultPasskeyEnrolName();
                setEnrollNameOpen(false);
                handleEnroll(chosen);
              }}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              {t("account.passkeys.enrollNameSkip")}
            </button>
            <button
              type="submit"
              disabled={!enrollNameDraft.trim()}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("account.passkeys.enrollNameContinue")}
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={renameTarget !== null}
        onClose={() => {
          if (!rename.isPending) setRenameTarget(null);
        }}
        title={t("account.passkeys.rename")}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (rename.isPending) return;
            const trimmed = newName.trim();
            if (!renameTarget || !trimmed || trimmed === renameTarget.name) return;
            rename.mutate(
              { passkeyId: renameTarget.id, name: trimmed },
              { onSuccess: () => setRenameTarget(null) },
            );
          }}
        >
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            aria-label={t("account.passkeys.nameLabel")}
            data-autofocus
            maxLength={100}
            readOnly={rename.isPending}
            aria-disabled={rename.isPending || undefined}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                if (!rename.isPending) setRenameTarget(null);
              }}
              aria-disabled={rename.isPending || undefined}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={
                !rename.isPending &&
                (!newName.trim() || !renameTarget || newName.trim() === renameTarget.name)
              }
              aria-disabled={rename.isPending || undefined}
              aria-busy={rename.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {rename.isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        closeOnConfirm={false}
        onConfirm={() => {
          if (deleteTarget) handleDeletePasskey(deleteTarget);
        }}
        title={t("account.passkeys.delete")}
        description={
          earlierRejection +
          (passkeys.length === 1 && !totpEnabled && deleteOverdue
            ? t("account.passkeys.deleteLastFactorOverdueConfirm", {
                name: deleteTarget?.name ?? "",
              })
            : passkeys.length === 1
              ? t("account.passkeys.deleteLastConfirm", { name: deleteTarget?.name ?? "" })
              : t("account.passkeys.deleteConfirm", { name: deleteTarget?.name ?? "" }))
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={remove.isPending}
      />

      <ConfirmDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        closeOnConfirm={false}
        onConfirm={handleRegenerate}
        title={t("account.passkeys.regenerate")}
        description={earlierRejection + t("account.passkeys.regenerateConfirm")}
        confirmLabel={t("account.passkeys.regenerate")}
        cancelLabel={t("common.cancel")}
        isPending={regenerate.isPending}
      />

      <Dialog
        open={visibleCodes !== null}
        onClose={dismissCodes}
        title={t("account.passkeys.recoveryCodesTitle")}
      >
        <p className="text-sm text-muted">{t("account.passkeys.recoveryCodesHint")}</p>
        <ul className="mt-4 grid grid-cols-1 gap-2 rounded-lg bg-elevated/50 p-4 font-mono text-sm text-heading sm:grid-cols-2">
          {(visibleCodes ?? []).map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCopyCodes}
            data-autofocus
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? t("account.passkeys.copied") : t("account.passkeys.copy")}
          </button>
          <button
            type="button"
            onClick={dismissCodes}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
          >
            {t("account.passkeys.codesSaved")}
          </button>
        </div>
      </Dialog>

      <StepUpDialog
        key={stepUp.nonce}
        open={stepUp.open}
        busy={stepUp.busy}
        error={stepUp.error}
        proofOnWire={stepUp.proofOnWire}
        actionNeedsDashboardSession={stepUp.dashboardOnly}
        serverDemandedProof={stepUp.serverDemandedProof}
        supportsWebAuthn={supportsWebAuthn}
        hasPasskey={passkeys.length > 0}
        hasTotp={totpEnabled}
        onPasskey={stepUp.withPasskey}
        onTotp={stepUp.withTotp}
        onCancel={stepUp.cancel}
        ceremonyOwner={ceremonyOwner}
        subtitle={
          enrollStepUpActive
            ? t("account.passkeys.enrollStepUpSubtitle")
            : t("account.stepUp.subtitle")
        }
        staleSessionBody={t("account.staleSession.body")}
        returnTo="/account"
      />
    </>
  );
}
